import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { Skeleton, useMediaQuery, useTheme } from "@mui/material";
import { headingFonts } from "../../../Styles/Styles";

// Register required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MonthlyOfferComparison = () => {
  const [chartData, setChartData] = useState(null);
  const [maxYValue, setMaxYValue] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/charts/monthly-offer-comparison`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          setError(`Error: ${response.status} - ${response.statusText}`);
          return;
        }

        const data = await response.json();

        const labels = data.map((stat) => stat.monthYear);
        const offersMade = data.map((stat) => stat.total || 0);
        const offersAccepted = data.map((stat) => stat.accepted || 0);
        const offersRejected = data.map((stat) => stat.rejected || 0);

        const totalMax = Math.max(
          ...offersMade,
          ...offersAccepted,
          ...offersRejected
        );
        setMaxYValue(totalMax * 2);

        setChartData({
          labels,
          datasets: [
            {
              label: "Offers Made",
              data: offersMade,
              backgroundColor: "rgba(75, 192, 192, 0.6)",
              borderColor: "rgba(75, 192, 192, 1)",
              borderWidth: 1,
            },
            {
              label: "Offers Accepted",
              data: offersAccepted,
              backgroundColor: "rgba(54, 162, 235, 0.6)",
              borderColor: "rgba(54, 162, 235, 1)",
              borderWidth: 1,
            },
            {
              label: "Offers Rejected",
              data: offersRejected,
              backgroundColor: "rgba(255, 99, 132, 0.6)",
              borderColor: "rgba(255, 99, 132, 1)",
              borderWidth: 1,
            },
          ],
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
        setError("An error occurred while fetching data.");
      }
    };

    fetchStats();
  }, [navigate]);

  return (
    <div style={{ width: "100%", padding: isMobile ? "10px" : "20px" }}>
      <h4
        style={{
          ...headingFonts,
          textAlign: "center",
        }}
      >
        Monthly Offer Comparison
      </h4>
      {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}

      {/* Skeleton Loader */}
      {!chartData && !error ? (
        <Skeleton variant="rectangular" width="100%" height={400} />
      ) : (
        chartData && (
          <Bar
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: isMobile ? "bottom" : "top",
                  labels: {
                    font: {
                      family: "ClashGrotesk-Medium",
                    },
                    letterSpacing: 1,
                  },
                },
                title: {
                  display: true,
                  text: "Monthly Comparison of Offers Made, Accepted, and Rejected",
                  font: {
                    family: "ClashGrotesk-Regular",
                  },
                  letterSpacing: 1,
                },
              },
              scales: {
                x: {
                  ticks: {
                    font: {
                      family: "ClashGrotesk-Medium",
                    },
                    letterSpacing: 1,
                  },
                },
                y: {
                  beginAtZero: true,
                  max: maxYValue,
                  ticks: {
                    font: {
                      family: "ClashGrotesk-Medium",
                    },
                    letterSpacing: 1,
                  },
                },
              },
            }}
            style={{
              ...headingFonts,
              maxHeight: isMobile ? "300px" : "500px",
            }}
          />
        )
      )}
    </div>
  );
};

export default MonthlyOfferComparison;
